import { useEffect, useRef, type ReactNode } from "react";
import { useLocation } from "wouter";
import { Menu } from "lucide-react";
import { cn } from "@client/lib/utils";
import { useUIStore } from "@client/store/uiStore";
import { Sidebar } from "@client/components/Sidebar";
import { AppHeader } from "@client/components/AppHeader";
import { ChannelDownBanner } from "@client/components/ChannelDownBanner";
import { Logo } from "@client/components/Logo";
import { useInboxStream } from "@client/hooks/useInboxStream";
import { useBrandTheme } from "@client/hooks/useBrandTheme";

/**
 * The shell owns the viewport: it is exactly one screen tall and never scrolls itself, so the sidebar
 * and the mobile header stay put while only `main` scrolls. That is what lets a screen like Conversas
 * ask for `h-full` and get a real inbox layout instead of a page that grows past the fold.
 *
 * Below `md` the sidebar is off-canvas, so the trigger to open it has to live out here — inside the
 * sidebar it would be unreachable exactly when it is needed.
 */
export function AppLayout({ children }: { children: ReactNode }) {
  const { sidebarCollapsed, mobileNavOpen, setMobileNavOpen } = useUIStore();
  const [location] = useLocation();

  // One stream for the whole signed-in app: Hoje, Fila and the inbox all move when a message lands.
  useInboxStream();

  // The tenant's own colour, which until now was saved and then never used anywhere.
  useBrandTheme();

  // Navigating is the implicit "done with the menu".
  useEffect(() => { setMobileNavOpen(false); }, [location, setMobileNavOpen]);

  /**
   * Start a new page at the top of it.
   *
   * `main` is the scroll container, not the window, so the browser's own scroll restoration never
   * applied and nothing else reset it. Scrolling halfway down the patient list and opening a
   * record landed you halfway down the record — which reads as the page having failed to render
   * rather than as navigation.
   *
   * `auto`, not `smooth`: a new page should already be at the top when you first see it, not
   * travel there while you are reading it.
   */
  const mainRef = useRef<HTMLElement>(null);
  useEffect(() => { mainRef.current?.scrollTo({ top: 0, behavior: "auto" }); }, [location]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setMobileNavOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileNavOpen, setMobileNavOpen]);

  return (
    <div className="h-dvh overflow-hidden bg-background">
      <Sidebar />

      {/*
        Scrim: only below md, where the sidebar floats over the content rather than beside it.

        It stays mounted and crossfades rather than being conditionally rendered. Unmounting it was
        instant while the drawer beside it slid for 180ms, so the two halves of one gesture were
        visibly out of step — the backdrop blinked away and then the menu left. `pointer-events` and
        `inert` follow the open state so a transparent scrim cannot swallow clicks or focus.
      */}
      <button
        type="button"
        aria-label="Fechar menu"
        tabIndex={mobileNavOpen ? 0 : -1}
        inert={mobileNavOpen ? undefined : true}
        onClick={() => setMobileNavOpen(false)}
        className={cn(
          "fixed inset-0 z-30 bg-black/50 md:hidden no-default-elevate",
          "transition-opacity duration-(--duration-slow) ease-(--ease-out)",
          mobileNavOpen ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />

      <div
        className={cn(
          "flex h-full flex-col md:transition-[padding] md:duration-(--duration-slow) md:ease-(--ease-out)",
          sidebarCollapsed ? "md:pl-16" : "md:pl-60",
        )}
      >
        {/* Phones: the same dark brand band as the sidebar it opens (and as the login's). */}
        <header className="flex h-14 shrink-0 items-center gap-2 bg-sidebar px-2 pt-[env(safe-area-inset-top)] text-white [--elevate-1:rgb(255_255_255/0.08)] [--elevate-2:rgb(255_255_255/0.14)] md:hidden">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Abrir menu"
            aria-expanded={mobileNavOpen}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl hover-elevate active-elevate-2"
          >
            <Menu className="h-5 w-5" strokeWidth={1.75} />
          </button>
          <Logo size="md" variant="light" />
        </header>

        {/* The desktop bar: search, identity and the account menu. Hides itself below md, where
            the mobile header above and the sidebar drawer cover the same ground. */}
        <AppHeader />

        {/* Above `main`, which is the scroll container — inside it the warning would scroll away
            from the very screen it is warning about. */}
        <ChannelDownBanner />

        <main ref={mainRef} className="min-h-0 flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
